import { difference, get, last, map, values } from "lodash"
import { ActionCreators } from "redux-undo"
import { call, Effect, put, select, takeLatest } from "redux-saga/effects"
import ActionTypes from "~/enums/ActionTypes"
import { checkTokenAndRedirect, doAPIRequest } from "../api"
import { post } from "~/utils/http"
import MessageTypes from "~/enums/MessageTypes"
import { createSnackbarMessage } from "../../actions/ui/snackbar"
import { createAction } from "~/utils/redux"
import { setTimelineUpdateLoading, unsetTimelineUpdateLoading } from "../../actions/timelines"
import { getSimulationId, getState, getUserData } from "../selectors"
import { API_SERVER_PLANNING_PROCESS } from "~/constants/api"
import { TimelineSagaResponse, TimelineSagaState } from "./TimelineSagaTypes"
import { AuthenticationState } from "~/types/ReduxStateExtensions"
import { User } from "~/types/api/UserTypes"
import { addOwner } from "~/reducers/timeline/helpers/addOwner"

interface RequestObj {
  currentOperation?: object
  versionIdentifier?: number
}

interface Error {
  response: {
    errorCode: string
    detailsObject: unknown
  }
}

interface Operation {
  operationId: number
  projectId: number
  productionOrderId: number
  position: number
  valueAdd: number
  resource: number
  resourceGroup: number
  startDate: string
  endDate: string
  status: number
  planTime: number
  pending: boolean
  articleAmount: number
  lotSize: number
  duration: number
  setupTime: number
  attendantOperation: boolean
  locked: boolean
}

const endPointOperationReplan = `${API_SERVER_PLANNING_PROCESS}/planningTimelineRaw/operation/successors/replan`
const endPointOperationCalculateDuration = `${API_SERVER_PLANNING_PROCESS}/planningTimelineRaw/operation/calculateDuration`

function* optimizeLeft() {
  const { timeline } = (yield select(getState)) as TimelineSagaState
  const updateObj: Record<string, object[]> = {}

  /* Detect the changed data items and send them to the API */
  updateObj.operations = difference(values(timeline.present.operations), values(last(timeline.past).operations))

  const numberOfChangedOperations = get(updateObj, "operations", []).length
  yield put(
    createSnackbarMessage(MessageTypes.INFO, "timeline.userMessage.updatedOperations", {
      values: numberOfChangedOperations,
    })
  )
}

function* optimizeRight() {
  const { timeline } = (yield select(getState)) as TimelineSagaState
  const changedObj: Record<string, object[]> = {}

  /* Detect the changed data items and send them to the API */
  changedObj.operations = difference(values(timeline.present.operations), values(last(timeline.past).operations))

  const numberOfChangedOperations = get(changedObj, "operations", []).length
  yield put(
    createSnackbarMessage(MessageTypes.INFO, "timeline.userMessage.updatedOperations", {
      values: numberOfChangedOperations,
    })
  )
}

function* calculateDuration(action: Effect<string, { operation: Operation }>) {
  const {
    payload: { operation },
  } = action
  const state = (yield select(getState)) as TimelineSagaState
  const { token } = (yield select(getUserData)) as AuthenticationState
  const simulationId = (yield select(getSimulationId)) as number
  const requestObj: RequestObj = {}
  requestObj.currentOperation = operation
  requestObj.versionIdentifier = get(state, "ui.timelines.versionIdentifier")

  try {
    yield put(setTimelineUpdateLoading())
    let resp: TimelineSagaResponse = null
    resp = (yield doAPIRequest({
      endPoint: endPointOperationCalculateDuration,
      method: post,
      prepareRequest: () => requestObj,
      token,
      state: { simulation: { id: simulationId } },
      payload: null,
      prepareResponse: null,
      prepareUrl: null,
    })) as TimelineSagaResponse
    const { projects: fetchedProjects, versionIdentifier } = resp
    const users: User[] = get(state, "entities.users")
    const projects = addOwner(fetchedProjects, users)

    // always update projects after replan
    const updatedProjects = map(resp.projects, "projectId")
    yield put(
      createAction(ActionTypes.TIMELINE_UPDATE_PROJECTS_SAVE_TO_PAST, {
        projects,
        affectedProjects: updatedProjects,
        users,
      })
    )
    yield put(
      createAction(ActionTypes.TIMELINE_VERSION_UPDATE, {
        versionIdentifier,
      })
    )
    yield put(unsetTimelineUpdateLoading())
  } catch (e) {
    yield call(handleApiError, e as Error)
    return
  }

  yield put(
    createAction(ActionTypes.ENTITIES_UPDATE_PERSISTS, {
      activities: [],
      operations: [],
      orderPositions: [],
      productionOrders: [],
      projects: [],
      valueAdds: [],
      ...requestObj,
    })
  )
}

function* rePlanOperation(action: Effect<string, { operation: Operation; replanCurrentOperation: boolean }>) {
  const {
    payload: { operation, replanCurrentOperation },
  } = action
  const state = (yield select(getState)) as TimelineSagaState
  const { token } = (yield select(getUserData)) as AuthenticationState
  const simulationId = (yield select(getSimulationId)) as number
  const requestObj: RequestObj = {}
  requestObj.currentOperation = { ...operation, replan: replanCurrentOperation }
  requestObj.versionIdentifier = get(state, "ui.timelines.versionIdentifier")

  try {
    yield put(setTimelineUpdateLoading())
    let resp: TimelineSagaResponse = null

    resp = (yield doAPIRequest({
      endPoint: endPointOperationReplan,
      method: post,
      prepareRequest: () => requestObj,
      token,
      state: { simulation: { id: simulationId } },
      payload: null,
      prepareResponse: null,
      prepareUrl: null,
    })) as TimelineSagaResponse

    const { projects: fetchedProjects, versionIdentifier } = resp
    const users: User[] = get(state, "entities.users")
    const projects = addOwner(fetchedProjects, users)

    // always update projects after replan
    const updatedProjects = map(resp.projects, "projectId")
    yield put(
      createAction(ActionTypes.TIMELINE_UPDATE_PROJECTS_SAVE_TO_PAST, {
        projects,
        affectedProjects: updatedProjects,
        users,
      })
    )
    yield put(
      createAction(ActionTypes.TIMELINE_VERSION_UPDATE, {
        versionIdentifier,
      })
    )
    yield put(unsetTimelineUpdateLoading())
  } catch (e) {
    yield call(handleApiError, e as Error)
    return
  }

  yield put(
    createAction(ActionTypes.ENTITIES_UPDATE_PERSISTS, {
      activities: [],
      operations: [],
      orderPositions: [],
      productionOrders: [],
      projects: [],
      valueAdds: [],
      ...requestObj,
    })
  )
}

const handleApiError = function* (e: Error) {
  const skipErrorHandling: unknown = yield call(checkTokenAndRedirect, e)
  if (skipErrorHandling) return
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const { errorCode, detailsObject } = e.response
  if (errorCode) {
    yield put(createSnackbarMessage(MessageTypes.ERROR, `errors.API.${errorCode}`, detailsObject))
  } else {
    yield put(createSnackbarMessage(MessageTypes.ERROR, "errors.API.unknown"))
  }

  yield put(ActionCreators.undo())
  yield put(ActionCreators.redo())
  yield put(unsetTimelineUpdateLoading())
}

export default [
  takeLatest(ActionTypes.TIMELINE_OPTIMIZE_OPERATIONS_LEFT, optimizeLeft),
  takeLatest(ActionTypes.TIMELINE_OPTIMIZE_OPERATIONS_RIGHT, optimizeRight),
  takeLatest(ActionTypes.TIMELINE_OPERATION_REPLAN, rePlanOperation),
  takeLatest(ActionTypes.TIMELINE_OPERATION_CALCULATE_DURATION, calculateDuration),
]
